import { Component, DestroyRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { DjiFlightAdminService } from '../../core/services/dji-flight-admin.service';
import { DjiDroneRef, DjiFlightPage, DjiFlightRow, DjiLinkStatus, OrganizationOption } from '../../core/models/dji-flight.model';
import {
  DJI_STATUS_TABS,
  DjiStatusTab,
  NO_ORGANIZATION,
  detailStatusLabel,
  formatDuration,
  formatInstant,
  rowHint,
  statusBadgeClass,
  statusLabel,
  tabCount,
  tabForStatuses,
  toListQuery,
} from '../../core/utils/dji-flight.utils';

const PAGE_SIZE = 25;

/**
 * DJI Flights (ADN-2451): DJI flights across every organization, including the
 * ones nobody can act on. The filters live in the URL, so a link from a drone
 * or organization page opens the list already narrowed, and a refresh or a
 * shared link keeps the view.
 */
@Component({
  selector: 'app-dji-flights',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './dji-flights.component.html',
  styleUrls: ['./dji-flights.component.scss'],
})
export class DjiFlightsComponent implements OnInit {
  readonly tabs = DJI_STATUS_TABS;
  readonly noOrganization = NO_ORGANIZATION;

  result: DjiFlightPage | null = null;
  organizations: OrganizationOption[] = [];
  loading = true;
  errorMessage = '';

  // Filters (read from the URL)
  tab: DjiStatusTab = DJI_STATUS_TABS[0];
  organizationId = '';
  droneId = '';
  droneLabel = '';
  from = '';
  to = '';
  page = 1;

  // Drone picker
  droneSearch = '';
  droneResults: DjiDroneRef[] = [];
  searchingDrones = false;
  private readonly droneSearchInput = new Subject<string>();

  constructor(
    private service: DjiFlightAdminService,
    private route: ActivatedRoute,
    private router: Router,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.service
      .listOrganizations()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (orgs) => (this.organizations = orgs), error: () => (this.organizations = []) });

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.readFilters(params);
      this.load();
    });

    this.droneSearchInput.pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe((text) => this.searchDrones(text));
  }

  get statusCounts() {
    return this.result?.statusCounts ?? null;
  }

  count(tab: DjiStatusTab): number {
    return tabCount(this.statusCounts, tab);
  }

  get pages(): number[] {
    const total = this.result?.totalPages ?? 0;
    const first = Math.max(1, Math.min(this.page - 2, total - 4));
    const last = Math.min(total, first + 4);
    return Array.from({ length: Math.max(0, last - first + 1) }, (_, i) => first + i);
  }

  get hasFilters(): boolean {
    return !!(this.organizationId || this.droneId || this.from || this.to);
  }

  // ---- filter changes: all go through the URL ---------------------------------

  selectTab(tab: DjiStatusTab): void {
    this.tab = tab;
    this.navigate(1);
  }

  onFilterChange(): void {
    this.navigate(1);
  }

  goToPage(page: number): void {
    if (page < 1 || page > (this.result?.totalPages ?? 1) || page === this.page) return;
    this.navigate(page);
  }

  clearFilters(): void {
    this.organizationId = '';
    this.droneId = '';
    this.droneLabel = '';
    this.from = '';
    this.to = '';
    this.droneSearch = '';
    this.navigate(1);
  }

  onDroneSearchInput(text: string): void {
    this.droneSearchInput.next(text.trim());
  }

  chooseDrone(drone: DjiDroneRef): void {
    this.droneId = drone.id;
    this.droneLabel = drone.serialNumber;
    this.droneSearch = '';
    this.droneResults = [];
    this.navigate(1);
  }

  clearDrone(): void {
    this.droneId = '';
    this.droneLabel = '';
    this.navigate(1);
  }

  open(row: DjiFlightRow): void {
    this.router.navigate(['/dji-flights', row.intakeId]);
  }

  reload(): void {
    this.load();
  }

  // ---- display helpers ---------------------------------------------------------

  statusLabel = statusLabel;
  statusBadgeClass = statusBadgeClass;
  detailStatusLabel = detailStatusLabel;
  rowHint = rowHint;
  formatDuration = formatDuration;
  formatInstant = formatInstant;

  // ---- internals ---------------------------------------------------------------

  private readFilters(params: ParamMap): void {
    const statuses = (params.get('status') ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean) as DjiLinkStatus[];
    this.tab = tabForStatuses(statuses);
    this.organizationId = params.get('organizationId') ?? '';
    this.droneId = params.get('droneId') ?? '';
    this.droneLabel = params.get('droneLabel') ?? '';
    this.from = params.get('from') ?? '';
    this.to = params.get('to') ?? '';
    const page = Number(params.get('page'));
    this.page = Number.isInteger(page) && page > 0 ? page : 1;
  }

  private navigate(page: number): void {
    const query: Record<string, string> = toListQuery({
      statuses: this.tab.statuses,
      organizationId: this.organizationId,
      droneId: this.droneId,
      from: this.from,
      to: this.to,
      page,
    });
    // The drone's label is only for display, so the chip still reads sensibly after a reload.
    if (this.droneId && this.droneLabel) query['droneLabel'] = this.droneLabel;
    this.router.navigate([], { relativeTo: this.route, queryParams: query });
  }

  private load(): void {
    this.loading = true;
    this.errorMessage = '';
    this.service
      .listFlights({
        statuses: this.tab.statuses,
        organizationId: this.organizationId,
        droneId: this.droneId,
        from: this.from,
        to: this.to,
        page: this.page,
        limit: PAGE_SIZE,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.result = result;
          this.loading = false;
        },
        error: (err: Error) => {
          this.errorMessage = err.message;
          this.loading = false;
        },
      });
  }

  private searchDrones(text: string): void {
    if (text.length < 2) {
      this.droneResults = [];
      return;
    }
    this.searchingDrones = true;
    this.service
      .searchDrones(text)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (drones) => {
          this.droneResults = drones;
          this.searchingDrones = false;
        },
        error: () => {
          this.droneResults = [];
          this.searchingDrones = false;
        },
      });
  }
}
